import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { Eye, EyeOff } from "lucide-react";
import { collection, query, where, getDoc, doc, getDocs, setDoc } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";

import { auth, db } from "./firebase";
import studentLogo from "./assets/student-logo.png";
import teacherLogo from "./assets/teacher-logo.png";
// import { Link } from "lucide-react";


// ----------------- Login Form -----------------
function LoginForm({ onRegister }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);


  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const uuid = user.uid;
      // console.log(user.uid);

      // Fetch user data from Firestore
      const userRef = doc(db, "users", uuid);
      const userSnap = await getDoc(userRef);

      let firstName = "";
      let lastName = "";
      let gradeLevel = "";
      let role = "";
      if (userSnap.exists()) {
        firstName = userSnap.data().first_name || "";
        lastName = userSnap.data().last_name || "";
        gradeLevel = userSnap.data().grade_level || "";
        role = userSnap.data().role || "";
      }



      // Save to localStorage
      localStorage.setItem("uuid", uuid);
      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);
      localStorage.setItem("grade_level", gradeLevel);
      localStorage.setItem("role", role);

      // Fetch and cache profile picture
      try {
        const storage = getStorage();
        const profilePicRef = ref(storage, `userProfile/${uuid}`);
        const url = await getDownloadURL(profilePicRef);
        localStorage.setItem("profilePicUrl", url);
      } catch (err) {
        localStorage.setItem("profilePicUrl", "");
      }


      if (role === "admin") {
        navigate("../pages/admin/account_management");
      } else if (role === "teacher") {
        navigate("../pages/teacher/students");
      } else if (role === "student") {
        navigate("../pages/student/home");
      }
      setError("");
    } catch (err) {
      setError("Incorrect login credentials.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <p className="text-gray-500 text-sm mb-6">
        Log in to continue your reading and learning journey
      </p>

      {error && (
        <div className="bg-red-100 text-red-600 p-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleLogin}>
        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">Email</label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your Email"
          />
        </div>

        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your Password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-secondary hover:bg-systemYellow-400 text-white text-lg font-bold shadow-sm hover:shadow transition-all disabled:opacity-50 flex items-center justify-center mt-4"
        >
          {loading ? (
            <svg
              className="animate-spin h-8 w-8 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
              ></path>
            </svg>
          ) : (
            "Login"
          )}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        Don't have an account yet?{" "}
        <button
          type="button"
          onClick={onRegister}
          className="text-blue-600 hover:underline font-semibold"
        >
          Register
        </button>
      </div>
    </>
  );
}


// ----------------- Class Code Form -----------------
function ClassCodeForm({ onCodeValidated, onSwitchToLogin }) {
  const [classCode, setClassCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const checkClassCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (classCode.trim() === "") {
      setError("Please enter a class code.");
      setLoading(false);
      return;
    }

    try {
      const q = query(collection(db, "classes"), where("class_code", "==", classCode));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("Invalid class code. Please check with your teacher.");
      } else {
        setError("");
        onCodeValidated(classCode); // ✅ Pass valid class code
      }
    } catch (err) {
      setError(err.message);
      console.error("Error validating class code:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <p className="text-gray-500 mb-6">
        To access the platform, please enter your class code from your teacher.
      </p>

      <form onSubmit={checkClassCode}>
        {error && (
          <div className="bg-red-100 text-red-600 p-2 rounded mb-4 text-sm text-left">
            {error}
          </div>
        )}
        <div className="mb-6 text-left">
          <label className="block mb-1 text-gray-700 text-sm">Class Code</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={classCode}
            onChange={(e) => setClassCode(e.target.value)}
            placeholder="Enter your Class Code"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-secondary hover:bg-systemYellow-400 text-white text-lg font-bold shadow-sm hover:shadow transition-all disabled:opacity-50 flex items-center justify-center mt-4">
          {loading ? (
            <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"></path>
            </svg>
          ) : (
            "Next"
          )}
        </button>
      </form>

      <div className="mt-4 text-sm">
        <span className="text-gray-600">Already have an account? </span>
        <button onClick={onSwitchToLogin} className="text-blue-500 hover:underline">
          Log-in instead
        </button>
      </div>
    </>
  );
}


// ----------------- Register Form -----------------
function RegisterForm({ classCode, onSwitchToLogin }) {
  // const navigate = useNavigate();
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [gender, setGender] = useState("");
  const [birthdate, setBirthDate] = useState("");
  const [address, setAddress] = useState("");
  const [school, setSchool] = useState("");
  const [grade_level, setGradeLevel] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation
    if (!email || !password || !confirmPassword) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      //  Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        first_name,
        last_name,
        gender,
        birthdate,
        address,
        school,
        role: "student",
        grade_level,
        email,
        classCode,
        createdAt: new Date(),

        // Assessment fields
        gst_score: 0,
        gst_total_questions: 0,
        gst_assessment_attempted: false,

        individualized_score: 0,
        individualized_assessment_attempted: false,
        createdAt: new Date(),
      });

      setError("");
      onSwitchToLogin()
      // navigate("/login");
    } catch (err) {
      setError(err.message);
      console.error("Registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <p className="text-gray-500 mb-6">Complete your registration below:</p>

      {error && (
        <div className="bg-red-100 text-red-600 p-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleRegister}>


        <div className="flex w-full">
          <div className="m-1 w-1/2">
            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">First Name</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={first_name}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                required
              />
            </div>


            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Gender</label>
              <select className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option>Select</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Grade Level</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={grade_level}
                onChange={(e) => setGradeLevel(e.target.value)}
                placeholder="Enter your grade level"
                required
              />
            </div>

            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Email</label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>


            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your Password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>



          </div>

          <div className="w-10"></div>
          <div className="m-1 w-1/2">
            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Last Name</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={last_name}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                required
              />
            </div>
            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Birthdate</label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={birthdate}
                onChange={(e) => setBirthDate(e.target.value)}
                placeholder="Enter your date of birth"
                required
              />
            </div>

            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">School</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                placeholder="Enter your school"
                required
              />
            </div>

            <div className="mb-4 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Address</label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your address"
                required
              />
            </div>

            <div className="mb-6 text-left">
              <label className="block mb-1 text-gray-700 text-sm">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your Password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-secondary hover:bg-systemYellow-400 text-white text-lg font-bold shadow-sm hover:shadow transition-all disabled:opacity-50 flex items-center justify-center mt-4">
          {loading ? (
            <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"></path>
            </svg>
          ) : (
            "Register"
          )}
        </button>
      </form>

      <div className="mt-4 text-sm">
        <span className="text-gray-600">Already have an account? </span>
        <button
          onClick={onSwitchToLogin}
          className="text-blue-500 hover:underline"
        >
          Log-in instead
        </button>
      </div>
    </>
  );
}




// ----------------- Register Teacher Form -----------------
function RegisterTeacherForm({ onSwitchToLogin }) {
  // const navigate = useNavigate();

  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [school, setSchool] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRegisterTeacher = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!email || !password || !confirmPassword) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      const user = userCredential.user;

      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        first_name,
        last_name,
        school,
        role: "teacher",
        email,
        createdAt: new Date(),
      });

      setError("");
      onSwitchToLogin();

    } catch (err) {
      setError(err.message);
      console.error("Teacher registration error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <p className="text-gray-500 mb-6">Register as a teacher:</p>

      {error && (
        <div className="bg-red-100 text-red-600 p-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleRegisterTeacher}>

        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">First Name</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-blue-400"
            value={first_name}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Enter your first name"
            required
          />
        </div>

        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">Last Name</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-blue-400"
            value={last_name}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Enter your last name"
            required
          />
        </div>

        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">School</label>
          <input
            type="text"
            className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-blue-400"
            value={school}
            onChange={(e) => setSchool(e.target.value)}
            placeholder="Enter your school"
            required
          />
        </div>

        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">Email</label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-blue-400"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your Email"
            required
          />
        </div>

        <div className="mb-4 text-left">
          <label className="block mb-1 text-gray-700 text-sm">Password</label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your Password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="mb-6 text-left">
          <label className="block mb-1 text-gray-700 text-sm">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className="w-full border border-gray-300 rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your Password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3.5 rounded-xl bg-secondary hover:bg-systemYellow-400 text-white text-lg font-bold shadow-sm hover:shadow transition-all disabled:opacity-50 flex items-center justify-center mt-4"
        >
          {loading ? (
            <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"></path>
            </svg>
          ) : (
            "Register Teacher"
          )}
        </button>
      </form>

      <div className="mt-4 text-sm">
        <span className="text-gray-600">Already have an account? </span>
        <button
          onClick={onSwitchToLogin}
          className="text-blue-500 hover:underline"
        >
          Log-in instead
        </button>
      </div>
    </>
  );
}


// ----------------- Main App -----------------
export default function App() {
  const [view, setView] = useState("login");
  const [validatedClassCode, setValidatedClassCode] = useState(null);

  const renderContent = () => {
    switch (view) {
      case "login":
        return <LoginForm onRegister={() => setView("registerUserType")} />;
      case "registerUserType":
        return (
          <>
            <p className="text-gray-500 text-base mb-6">Choose your user type:</p>

            <div className="flex justify-center space-x-6">
              {/* Teacher */}
              <div
                onClick={() => setView("registerTeacher")} // for now
                className="flex flex-col items-center p-6 bg-purple-100 rounded-2xl shadow-md cursor-pointer hover:bg-purple-200 transition-colors"
              >
                <img src={teacherLogo} alt="Teacher" className="w-16 h-16 mb-4 object-contain" />
                <span className="font-semibold text-lg">Teacher</span>
              </div>

              {/* Student */}
              <div
                onClick={() => setView("registerClassCode")}
                className="flex flex-col items-center p-6 bg-orange-100 rounded-2xl shadow-md cursor-pointer hover:bg-orange-200 transition-colors"
              >
                <img src={studentLogo} alt="Student" className="w-16 h-16 mb-4 object-contain" />
                <span className="font-semibold text-lg">Student</span>
              </div>
            </div>
          </>
        );
      case "registerClassCode":
        return (
          <ClassCodeForm
            onCodeValidated={(classCode) => {
              setValidatedClassCode(classCode);
              setView("registerForm");
            }}
            onSwitchToLogin={() => setView("login")}
          />
        );
      case "registerForm":
        return (
          <RegisterForm
            classCode={validatedClassCode}
            onSwitchToLogin={() => setView("login")}
          />
        );
      case "registerTeacher":
        return (
          <RegisterTeacherForm
            onSwitchToLogin={() => setView("login")}
          />
        );
      default:
        return <LoginForm />;
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 p-4">
      <div className={`bg-white p-8 rounded-3xl shadow-soft w-full ${view === 'registerForm' ? 'max-w-2xl' : 'max-w-md'} text-center border border-slate-100`}>
        <img src="assets/logo.png" alt="logo" className="w-32 block mx-auto mb-2" />
        <h2 className="text-3xl font-extrabold mb-4 text-slate-800">
          {view === "login" ? "Welcome back!" : "Welcome!"}
        </h2>
        <div className="flex justify-center mb-8 space-x-3">
          <button
            type="button"
            onClick={() => setView("login")}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${view === "login"
              ? "text-white bg-secondary shadow-sm"
              : "text-slate-500 bg-slate-100 hover:bg-slate-200"
              }`}
          >
            Login
          </button>
          <button
            type="button"
            onClick={() => setView("registerUserType")}
            className={`px-6 py-2.5 rounded-xl font-medium transition-all ${view !== "login"
              ? "text-white bg-secondary shadow-sm"
              : "text-slate-500 bg-slate-100 hover:bg-slate-200"
              }`}
          >
            Register
          </button>
        </div>
        {renderContent()}
      </div>
    </div>
  );
}
